import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BetaBadge } from "@/components/BetaBadge";
import { KalibLogo } from "@/components/KalibLogo";
import { toast } from "sonner";

/**
 * AccountSettingsPage — /settings/account
 *
 * Houses the non-Reveal-calibration user identity fields that used to live at
 * the bottom of /settings/profile:
 *   • Account email   (read-only)
 *   • First name      (editable)
 *   • Password        (change-password form)
 *   • Subscription    (placeholder)
 *
 * /settings/profile is now strictly the Reveal-calibration surface.
 */
export default function AccountSettingsPage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  // Phase 4 — Weekly Reveal email opt-out. Defaults to opted-in (true) for
  // legacy users with no `notification_preferences` row yet.
  const [weeklyEmail, setWeeklyEmail] = useState(true);
  const [savingEmailPref, setSavingEmailPref] = useState(false);

  useEffect(() => {
    if (user === null) { navigate("/signin"); return; }
    if (user) {
      setFirstName(user.first_name || "");
      const prefs = user.notification_preferences || {};
      // Treat missing flag as opted-in.
      setWeeklyEmail(prefs.weekly_reveal_email !== false);
    }
  }, [user, navigate]);

  const nameDirty = user && (firstName || "") !== (user.first_name || "");

  const handleSaveName = async () => {
    if (!nameDirty || savingName) return;
    setSavingName(true);
    try {
      await API.put("/settings/profile", { first_name: firstName });
      updateUser({ first_name: firstName });
      toast.success("First name updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingName(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirmation don't match.");
      return;
    }
    setChangingPassword(true);
    try {
      await API.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password updated.");
      setShowPasswordForm(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setChangingPassword(false);
    }
  };

  const handleToggleWeeklyEmail = async () => {
    if (savingEmailPref) return;
    const next = !weeklyEmail;
    setWeeklyEmail(next); // optimistic
    setSavingEmailPref(true);
    try {
      await API.put("/settings/profile", { weekly_reveal_email: next });
      updateUser({
        notification_preferences: {
          ...(user?.notification_preferences || {}),
          weekly_reveal_email: next,
        },
      });
      toast.success(next ? "You'll receive the weekly Reveal email." : "Weekly Reveal email turned off.");
    } catch (err) {
      // Revert on failure
      setWeeklyEmail(!next);
      toast.error(formatApiError(err));
    } finally {
      setSavingEmailPref(false);
    }
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <p className="text-sm text-zinc-900">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="account-settings-page">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/" className="block" data-testid="account-settings-logo-link" aria-label="Kalib AI home">
              <KalibLogo variant="light" height={28} testId="account-settings-header-logo" />
            </Link>
            <BetaBadge />
          </div>
          <Link to="/intelligence" className="text-sm text-zinc-900 hover:text-zinc-950 transition-colors" data-testid="account-back-link">
            Back to Reveal
          </Link>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-zinc-950 font-heading mb-2" data-testid="account-settings-title">
          Account settings
        </h1>
        <p className="text-sm text-zinc-900 mb-10" data-testid="account-settings-subtitle">
          Your identity and sign-in details. For Reveal calibration,{" "}
          <Link to="/settings/profile" className="text-amber-700 hover:text-amber-800 underline" data-testid="account-to-reveal-link">
            open Reveal setup
          </Link>
          .
        </p>

        <section className="space-y-10">
          {/* Email (read-only) */}
          <div data-testid="account-email-block">
            <label className="text-xs font-medium uppercase tracking-widest text-zinc-900 block mb-1.5">Account email</label>
            <p className="text-sm text-zinc-950 bg-zinc-50 border border-zinc-200 px-3 py-2" data-testid="account-email-value">{user?.email}</p>
          </div>

          {/* First name */}
          <div data-testid="account-first-name-block">
            <label className="text-xs font-medium uppercase tracking-widest text-zinc-900 block mb-1.5">First name</label>
            <div className="flex items-center gap-3">
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="rounded-none border-zinc-200 max-w-xs focus:ring-amber-700 focus:border-amber-700"
                data-testid="account-first-name-input"
              />
              <Button
                onClick={handleSaveName}
                disabled={!nameDirty || savingName}
                className="bg-amber-700 text-white hover:bg-amber-800 rounded-none text-sm font-medium px-4 py-2 h-auto disabled:opacity-40"
                data-testid="account-first-name-save-btn"
              >
                {savingName ? "Saving…" : "Save"}
              </Button>
            </div>
            <p className="text-[12px] text-zinc-700 mt-2">Used in your Reveal greeting and prompt tone.</p>
          </div>

          {/* Password */}
          <div data-testid="account-password-block">
            <label className="text-xs font-medium uppercase tracking-widest text-zinc-900 block mb-1.5">Password</label>
            {!showPasswordForm ? (
              <div className="flex items-center justify-between gap-4">
                <p className="text-sm text-zinc-900">Signed in as <span className="font-medium text-zinc-950">{user?.email}</span></p>
                <button
                  type="button"
                  onClick={() => setShowPasswordForm(true)}
                  className="text-sm text-amber-700 hover:text-amber-800 font-medium whitespace-nowrap"
                  data-testid="account-change-password-btn"
                >
                  Change password →
                </button>
              </div>
            ) : (
              <form onSubmit={handleChangePassword} className="bg-zinc-50 border border-zinc-200 p-4 space-y-3" data-testid="account-password-form">
                <div>
                  <label className="text-[11px] font-medium uppercase tracking-widest text-zinc-900 mb-1 block">Current password</label>
                  <Input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="rounded-none border-zinc-200 focus:ring-amber-700 focus:border-amber-700"
                    data-testid="account-current-password-input"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-medium uppercase tracking-widest text-zinc-900 mb-1 block">New password (min 6 chars)</label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={6}
                    required
                    className="rounded-none border-zinc-200 focus:ring-amber-700 focus:border-amber-700"
                    data-testid="account-new-password-input"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-medium uppercase tracking-widest text-zinc-900 mb-1 block">Confirm new password</label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    minLength={6}
                    required
                    className="rounded-none border-zinc-200 focus:ring-amber-700 focus:border-amber-700"
                    data-testid="account-confirm-password-input"
                  />
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    type="submit"
                    disabled={changingPassword}
                    className="bg-amber-700 text-white hover:bg-amber-800 rounded-none text-sm font-medium px-4 py-2 h-auto"
                    data-testid="account-password-submit-btn"
                  >
                    {changingPassword ? "Updating..." : "Update password"}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      setShowPasswordForm(false);
                      setCurrentPassword("");
                      setNewPassword("");
                      setConfirmPassword("");
                    }}
                    variant="outline"
                    className="rounded-none border-zinc-300 text-zinc-700 hover:bg-zinc-50 text-sm font-medium px-4 py-2 h-auto"
                    data-testid="account-password-cancel-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>

          {/* Notifications — Phase 4 weekly Reveal email opt-out */}
          <div data-testid="account-notifications-block">
            <label className="text-xs font-medium uppercase tracking-widest text-zinc-900 block mb-1.5">Notifications</label>
            <div className="bg-zinc-50 border border-zinc-200 px-4 py-3 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-zinc-950" data-testid="account-weekly-email-title">
                  Weekly Reveal email
                </p>
                <p className="text-[12px] text-zinc-700 mt-0.5">
                  We'll email you each week when your new Reveal is ready. You can flip this anytime.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={weeklyEmail}
                aria-label="Toggle weekly Reveal email"
                onClick={handleToggleWeeklyEmail}
                disabled={savingEmailPref}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center transition-colors focus:outline-none focus:ring-2 focus:ring-amber-700 focus:ring-offset-2 disabled:opacity-50 ${
                  weeklyEmail ? "bg-amber-700" : "bg-zinc-300"
                }`}
                data-testid="account-weekly-email-toggle"
              >
                <span
                  className={`inline-block h-5 w-5 transform bg-white shadow transition-transform ${
                    weeklyEmail ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Subscription */}
          <div data-testid="account-subscription-block">
            <label className="text-xs font-medium uppercase tracking-widest text-zinc-900 block mb-1.5">Subscription</label>
            <p className="text-sm text-zinc-900">Manage subscription — coming soon.</p>
          </div>
        </section>
      </div>
    </div>
  );
}
